import React, { useEffect, useState } from "react";
import { Button, Card, Form, Spinner } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useParams } from "react-router-dom";

import { useAppApi } from "@/App";
import { useRequest } from "@components/AppHook";
import { DevForm } from "@components/Forms/Fields";
import { buildFormData } from "@components/Forms/Handlers";
import { DefaultLayout } from "@components/Layouts";
import { scroll_to_top, url_reverse } from "@components/Utils";

export default function EventSendNewsletterScreen() {
    const { t } = useTranslation();
    const { event_slug, newsletter_id } = useParams();
    const apiUrl = url_reverse("api:event:send_newsletter", { event_slug });
    const editApiUrl = url_reverse("api:event:send_newsletter:edit", {
        event_slug,
        newsletter_id,
    });
    const navigate = useNavigate();
    const url = newsletter_id ? editApiUrl : apiUrl;
    const { apiData, setApiData } = useAppApi(url);
    const { api_post_formdata } = useRequest();
    const letter_type = apiData?.forms_data?.main?.data?.type;
    const [isLoading, setIsLoading] = useState(false);

    const toolTipData = {
        participant: t("Рассылка отправится играм, которые ранее получили письмо о начале мероприятия."),
        new_participant: t(
            "Рассылка, которая информирует о начале мероприятия и одновременно создает раздел с ивентом в кабинете разработчика. После отправки менять настройку запрашиваемых полей нельзя."
        ),
        not_accepted_participation: t(
            "Рассылка отправится играм, которые получили рассылку о начале ивента, но еще не приняли участие."
        ),
        material_rejected: t(
            "Рассылка отправится играм, которые получили рассылку о начале ивента, приняли участие, но не прошли модерацию."
        ),
        non_participant: t("Рассылка отправится играм, которых нет в ивенте."),
    };

    useEffect(() => {
        if (letter_type !== apiData?.newsletter?.type && apiData?.newsletters_info[letter_type]) {
            navigate(
                url_reverse("admin:event:send_newsletter:edit", {
                    event_slug,
                    newsletter_id: apiData?.newsletters_info[letter_type],
                })
            );
        }
        if (letter_type && !apiData?.newsletters_info[letter_type] && newsletter_id) {
            navigate(url_reverse("admin:event:send_newsletter", { event_slug }));
        }
        if (apiData?.forms_data) {
            const newApiData = { ...apiData };
            if (letter_type === "material_rejected") {
                newApiData.forms_data.main.fields.letter_subject.required = false;
                newApiData.forms_data.main.fields.letter_subject_en.required = false;
                newApiData.forms_data.main.fields.letter_subject.disabled = true;
                newApiData.forms_data.main.data.letter_subject = "";
                newApiData.forms_data.main.fields.letter_subject_en.disabled = true;
                newApiData.forms_data.main.data.letter_subject_en = "";
            } else {
                newApiData.forms_data.main.fields.letter_subject.required = true;
                newApiData.forms_data.main.fields.letter_subject_en.required = true;
                newApiData.forms_data.main.fields.letter_subject.disabled = false;
                newApiData.forms_data.main.fields.letter_subject_en.disabled = false;
            }
            newApiData.forms_data.main.fields.type.widget.attrs.tooltip_data = toolTipData[letter_type];
            setApiData(newApiData);
        }
    }, [letter_type]);

    const onSubmit = (ev) => {
        ev.preventDefault();
        const formData = buildFormData({
            ev,
            apiData,
            submitPrefixes: ["main"],
        });
        const action = ev.nativeEvent.submitter.name;
        setIsLoading(action === "send_newsletter");
        formData.append("action", action);

        api_post_formdata(ev, url, formData, {
            then: (resp) => {
                if (resp.status === "ok") {
                    setIsLoading(false);
                    setApiData(resp);
                    if (action === "save_newsletter_and_continue") {
                        if (!newsletter_id) {
                            navigate(
                                url_reverse("admin:event:send_newsletter:edit", {
                                    event_slug,
                                    newsletter_id: resp.newsletter.id,
                                })
                            );
                        }
                    } else {
                        navigate(url_reverse("admin:event:items", { slug: event_slug }));
                    }
                } else if (resp.status === "fail") {
                    if (letter_type === "material_rejected") {
                        resp.forms_data.main.fields.letter_subject.required = false;
                        resp.forms_data.main.fields.letter_subject_en.required = false;
                        resp.forms_data.main.fields.letter_subject.disabled = true;
                        resp.forms_data.main.data.letter_subject = "";
                        resp.forms_data.main.fields.letter_subject_en.disabled = true;
                        resp.forms_data.main.data.letter_subject_en = "";
                    }
                    setApiData(resp);
                    setIsLoading(false);
                    scroll_to_top();
                }
            },
        });
    };
    const fieldProps = {
        apiData,
        setApiData,
    };
    const header = (
        <>
            <Link to={url_reverse("admin:event:items", { slug: event_slug })}>{t("Управление ивентом")}</Link>
            <span> – </span>
            {t("Рассылка")}
        </>
    );
    return (
        <DefaultLayout>
            <Card>
                <Card.Header>{header}</Card.Header>
                <Card.Body>
                    {Object.entries(apiData?.errors || {}).map(([key, value]) => {
                        return (
                            <div key={key} className="dmg-callout dmg-callout-danger mb-3">
                                <div dangerouslySetInnerHTML={{ __html: value }} />
                            </div>
                        );
                    })}
                    {apiData?.forms_data?.main?.fields && (
                        <Form onSubmit={onSubmit}>
                            <DevForm.Field {...fieldProps} prefix="main" name="letter_subject" />
                            <DevForm.Field {...fieldProps} prefix="main" name="event_description" />
                            <DevForm.Field {...fieldProps} prefix="main" name="letter_subject_en" />
                            <DevForm.Field {...fieldProps} prefix="main" name="event_description_en" />
                            <DevForm.Field {...fieldProps} prefix="main" name="type" colField={4} />
                            <DevForm.Field {...fieldProps} prefix="main" name="emails" />
                            <DevForm.Field {...fieldProps} prefix="main" name="is_test" />
                            <DevForm.Button variant="primary" name="save_newsletter" type="submit">
                                {t("Сохранить")}
                            </DevForm.Button>{" "}
                            <DevForm.Button variant="primary" name="save_newsletter_and_continue" type="submit">
                                {t("Сохранить и продолжить")}
                            </DevForm.Button>{" "}
                            <DevForm.Button variant="primary" name="send_newsletter" type="submit">
                                {isLoading && (
                                    <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
                                )}
                                {t("Отправить рассылку")}
                            </DevForm.Button>
                            <div className="float-right">
                                <Button
                                    variant="warning"
                                    onClick={() =>
                                        navigate(
                                            url_reverse("admin:event:newsletter_history:list", {
                                                event_slug,
                                            })
                                        )
                                    }
                                >
                                    {t("История рассылок")}
                                </Button>{" "}
                            </div>
                        </Form>
                    )}
                </Card.Body>
            </Card>
        </DefaultLayout>
    );
}
